import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Gallery.css';

const bootLines = [
    '> ACCESSING ROTB ARCHIVE...',
    '> BYPASSING ENCRYPTION... OK',
    '> 16 FILES RECOVERED',
    '> ACCESS GRANTED'
];

// Videos and photos alternate for as long as both types are available
const mediaItems = [
    {
        id: 1,
        type: 'video',
        src: '/gallery/clip_3940.mp4',
        label: 'REC_3940.RAW',
        phase: 'PHASE 5 — REFLECTIONS',
        date: '28.02.25'
    },
    {
        id: 2,
        type: 'image',
        src: '/gallery/frontline.jpeg',
        label: 'FRONTLINE.IMG',
        phase: 'PHASE 5 — REFLECTIONS',
        date: '28.02.25'
    },
    {
        id: 3,
        type: 'video',
        src: '/gallery/clip_4005.mp4',
        label: 'REC_4005.RAW',
        phase: 'PHASE 5 — REFLECTIONS',
        date: '01.03.25'
    },
    {
        id: 4,
        type: 'image',
        src: '/gallery/sideview.jpeg',
        label: 'SIDEVIEW.IMG',
        phase: 'PHASE 5 — REFLECTIONS',
        date: '28.02.25'
    },
    {
        id: 5,
        type: 'video',
        src: '/gallery/clip_3994.mp4',
        label: 'REC_3994.RAW',
        phase: 'PHASE 5 — REFLECTIONS',
        date: '01.03.25'
    },
    {
        id: 6,
        type: 'image',
        src: '/gallery/buildup.jpeg',
        label: 'BUILDUP.IMG',
        phase: 'PHASE 6 — ANCHOR NYE',
        date: '31.12.25'
    },
    {
        id: 7,
        type: 'video',
        src: '/gallery/clip_3996.mp4',
        label: 'REC_3996.RAW',
        phase: 'PHASE 5 — REFLECTIONS',
        date: '01.03.25'
    },
    {
        id: 8,
        type: 'image',
        src: '/gallery/dj.jpeg',
        label: 'DJ.IMG',
        phase: 'PHASE 7 — ASCENSION',
        date: '15.03.26'
    },
    {
        id: 9,
        type: 'video',
        src: '/gallery/clip_2662.mp4',
        label: 'REC_2662.RAW',
        phase: 'PHASE 6 — ANCHOR NYE',
        date: '31.12.25'
    },
    {
        id: 10,
        type: 'image',
        src: '/gallery/midnight.jpeg',
        label: 'MIDNIGHT.IMG',
        phase: 'PHASE 6 — ANCHOR NYE',
        date: '01.01.26'
    },
    {
        id: 11,
        type: 'image',
        src: '/gallery/banner.jpeg',
        label: 'BANNER.IMG',
        phase: 'PHASE 6 — ANCHOR NYE',
        date: '31.12.25'
    },
    {
        id: 12,
        type: 'image',
        src: '/gallery/statue.jpeg',
        label: 'STATUE.IMG',
        phase: 'PHASE 7 — ASCENSION',
        date: '14.03.26'
    },
    {
        id: 13,
        type: 'image',
        src: '/gallery/apparition.jpeg',
        label: 'APPARITION.IMG',
        phase: 'PHASE 7 — ASCENSION',
        date: '15.03.26'
    },
    {
        id: 14,
        type: 'image',
        src: '/gallery/shrine.jpeg',
        label: 'SHRINE.IMG',
        phase: 'PHASE 7 — ASCENSION',
        date: '14.03.26'
    },
    {
        id: 15,
        type: 'image',
        src: '/gallery/booth.jpeg',
        label: 'BOOTH.IMG',
        phase: 'PHASE 7 — ASCENSION',
        date: '15.03.26'
    },
    {
        id: 16,
        type: 'image',
        src: '/gallery/backprint.jpeg',
        label: 'BACKPRINT.IMG',
        phase: 'PHASE 3 — SOLSTICE',
        date: '06.07.24'
    }
];

function Gallery() {
    const navigate = useNavigate();
    const [typedBootText, setTypedBootText] = useState('');
    const [bootLineIndex, setBootLineIndex] = useState(0);
    const [terminalFading, setTerminalFading] = useState(false);
    const [bootComplete, setBootComplete] = useState(false);
    const [visibleItems, setVisibleItems] = useState(0);
    const [lightboxItem, setLightboxItem] = useState(null);

    // Terminal boot sequence typing effect
    useEffect(() => {
        if (bootLineIndex >= bootLines.length) {
            // Hold on "ACCESS GRANTED" briefly, then fade the terminal out
            const fadeTimer = setTimeout(() => setTerminalFading(true), 700);
            const doneTimer = setTimeout(() => setBootComplete(true), 1300);
            return () => {
                clearTimeout(fadeTimer);
                clearTimeout(doneTimer);
            };
        }

        const currentLine = bootLines[bootLineIndex];
        const typedSoFar = typedBootText.split('\n').pop() || '';

        if (typedSoFar.length < currentLine.length) {
            const timer = setTimeout(() => {
                setTypedBootText(prev => {
                    const lines = prev.split('\n');
                    lines[bootLineIndex] = currentLine.substring(0, typedSoFar.length + 1);
                    return lines.join('\n');
                });
            }, 25);
            return () => clearTimeout(timer);
        }

        const timer = setTimeout(() => {
            setBootLineIndex(prev => prev + 1);
            setTypedBootText(prev => prev + '\n');
        }, 250);
        return () => clearTimeout(timer);
    }, [typedBootText, bootLineIndex]);

    // Reveal media tiles one by one after boot
    useEffect(() => {
        if (!bootComplete || visibleItems >= mediaItems.length) return;

        const timer = setTimeout(() => {
            setVisibleItems(prev => prev + 1);
        }, 350);
        return () => clearTimeout(timer);
    }, [bootComplete, visibleItems]);

    const closeLightbox = useCallback(() => setLightboxItem(null), []);

    // Close lightbox on Escape
    useEffect(() => {
        if (!lightboxItem) return;

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') closeLightbox();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [lightboxItem, closeLightbox]);

    return (
        <div className="gallery-page">
            <div className="gallery-container">
                <h1 className="gallery-title">ARCHIVE</h1>
                <div className="gallery-contribute">
                    <p>YOUR NIGHT. YOUR PERSPECTIVE.</p>
                    <Link to="/dropzone" className="gallery-upload-link">↑ UPLOAD YOUR PHOTOS & VIDEOS</Link>
                </div>

                {/* Terminal boot sequence (disappears once access is granted) */}
                {!bootComplete && (
                    <div className={`gallery-terminal ${terminalFading ? 'fading' : ''}`}>
                        {typedBootText.split('\n').map((line, index) => (
                            <div key={index} className="terminal-line">
                                {line}
                                {index === bootLineIndex && (
                                    <span className="terminal-cursor">█</span>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Media grid */}
                {bootComplete && (
                    <div className="gallery-grid">
                        {mediaItems.slice(0, visibleItems).map(item => (
                            <div
                                key={item.id}
                                className="gallery-card"
                                onClick={() => setLightboxItem(item)}
                            >
                                <div className="gallery-media-wrapper">
                                    {item.type === 'video' ? (
                                        <video
                                            autoPlay
                                            muted
                                            loop
                                            playsInline
                                            className="gallery-media"
                                        >
                                            <source src={item.src} type="video/mp4" />
                                        </video>
                                    ) : (
                                        <img
                                            src={item.src}
                                            alt={item.label}
                                            className="gallery-media"
                                        />
                                    )}
                                    <div className="gallery-scanlines"></div>
                                    <div className="gallery-card-type">
                                        {item.type === 'video' ? '▶ VID' : '□ IMG'}
                                    </div>
                                </div>
                                <div className="gallery-card-meta">
                                    <span className="gallery-card-label">{item.label}</span>
                                    <span className="gallery-card-date">{item.date}</span>
                                </div>
                                <div className="gallery-card-phase">{item.phase}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Lightbox */}
            {lightboxItem && (
                <div className="gallery-lightbox" onClick={closeLightbox}>
                    <button className="lightbox-close" onClick={closeLightbox}>
                        [ X ] CLOSE
                    </button>
                    <div
                        className="lightbox-content"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {lightboxItem.type === 'video' ? (
                            <video
                                autoPlay
                                loop
                                controls
                                playsInline
                                className="lightbox-media"
                            >
                                <source src={lightboxItem.src} type="video/mp4" />
                            </video>
                        ) : (
                            <img
                                src={lightboxItem.src}
                                alt={lightboxItem.label}
                                className="lightbox-media"
                            />
                        )}
                        <div className="lightbox-meta">
                            <span>{lightboxItem.label}</span>
                            <span>{lightboxItem.phase}</span>
                            <span>{lightboxItem.date}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Navigation */}
            <div className="gallery-navigation">
                <button className="home-btn" onClick={() => navigate('/')}>
                    HOME
                </button>
            </div>
        </div>
    );
}

export default Gallery;
